import * as React from "react";
import { Control, FieldValues, Path, useWatch } from "react-hook-form";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { addMonths, format } from "date-fns";

import LabeledCheckboxField from "components/labeled-checkbox-field";
import {
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_ON_LOAN
} from "constants/patient-products";
import { formatDateOnlyValue } from "utils/common";

export interface WarrantyDatesOnDeliveryPreviewProduct {
  id: number;
  description?: string;
  status?: number;
  manufacturer_warranty_expiration_date?: string | null;
  loss_damage_warranty_expiration_date?: string | null;
  /** Loss & Damage replacement (SN-7365): warranties were carried over from this device. */
  replaced_patient_product_detail?: { serial_number?: string | null } | null;
  loss_damage_used_at_display?: string | null;
  inventory_product?: {
    product?: {
      display_name?: string | null;
      specification?: {
        warranty_months?: number | null;
        loss_damage_warranty_months?: number | null;
      } | null;
    } | null;
  } | null;
}

interface WarrantyDatesOnDeliveryControlProps<
  T extends FieldValues & { update_warranty_dates: boolean }
> {
  products: WarrantyDatesOnDeliveryPreviewProduct[];
  deliveredAt: Date | null;
  control: Control<T>;
}

const isDeliverable = (product: WarrantyDatesOnDeliveryPreviewProduct) =>
  product.status !== PATIENT_PRODUCT_STATUS_DELIVERED &&
  product.status !== PATIENT_PRODUCT_STATUS_ON_LOAN;

const formatProposedDate = (
  deliveredAt: Date | null,
  months: number | null | undefined
) => {
  if (!deliveredAt || !months) return "-";
  return format(addMonths(deliveredAt, months), "MMM dd, yyyy");
};

/**
 * Shows what will actually happen to one warranty date when the checkbox is checked:
 * "Current → New" if it changes, "→ New" if there's no current date, just the date if
 * it isn't changing, and the current date untouched when there's no warranty duration
 * configured to compute a new one from (checking the box has nothing to recalculate).
 */
const formatWarrantyTransition = (
  currentDate: string | null | undefined,
  deliveredAt: Date | null,
  months: number | null | undefined
) => {
  const current = formatDateOnlyValue(currentDate, "MMM dd, yyyy", "");

  if (!months) return current || "-";

  const proposed = formatProposedDate(deliveredAt, months);
  if (proposed === "-") return current || "-";
  if (!current) return `→ ${proposed}`;
  if (current === proposed) return current;
  return `${current} → ${proposed}`;
};

/**
 * Per-delivery checkbox + preview table (ENG-1658): shows each device's current
 * warranty dates next to the dates that would be applied. Pure presentation —
 * the product list and delivered-at date are fetched/owned by the caller.
 */
export function WarrantyDatesOnDeliveryControl<
  T extends FieldValues & { update_warranty_dates: boolean }
>({ products, deliveredAt, control }: WarrantyDatesOnDeliveryControlProps<T>) {
  const updateWarrantyDates = useWatch({
    control,
    name: "update_warranty_dates" as Path<T>
  });

  const previewProducts = products.filter((product) => {
    if (!isDeliverable(product)) return false;
    // An L&D replacement is always listed so the carry-over is visible, even
    // when the catalog has no warranty duration to recalculate from.
    if (product.replaced_patient_product_detail) return true;
    const specification = product.inventory_product?.product?.specification;
    return Boolean(
      specification?.warranty_months ||
        specification?.loss_damage_warranty_months
    );
  });
  const replacementProducts = previewProducts.filter((product) =>
    Boolean(product.replaced_patient_product_detail)
  );

  if (!previewProducts.length) {
    const emptyReason = !products.length
      ? "No active hearing aids were found for this patient, so there are no warranty dates to preview."
      : !products.some(isDeliverable)
        ? "All active devices are already marked delivered or out on loan, so there are no warranty dates left to update."
        : "None of the active devices have a manufacturer or loss & damage warranty duration configured in the catalog, so there's nothing to preview.";

    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {emptyReason}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: updateWarrantyDates ? 2 : 0 }}>
        <LabeledCheckboxField
          name={"update_warranty_dates" as Path<T>}
          control={control}
          testId="update_warranty_dates"
          label="Update Warranty Dates From the Delivery Date"
          description="When checked, MFR and L&D warranty dates below are recalculated from the Delivered At date and replace any existing dates. When unchecked, existing dates are kept — only blank dates are filled in. It starts from your practice's default setting."
        />
      </Box>
      {replacementProducts.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
          {replacementProducts
            .map(
              (product) =>
                `${product.inventory_product?.product?.display_name || product.description || "Device"} replaces SN ${product.replaced_patient_product_detail?.serial_number || "—"} under Loss & Damage`
            )
            .join("; ")}
          . Its manufacturer and extended warranty dates were carried over from
          the original device and are not updated on delivery
          {replacementProducts[0]?.loss_damage_used_at_display
            ? ` (L&D used on ${replacementProducts[0].loss_damage_used_at_display})`
            : ""}
          .
        </Alert>
      )}
      {updateWarrantyDates && (
        <TableContainer
          sx={{
            overflowX: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{ "& th": { bgcolor: "grey.50", fontWeight: 600 } }}
              >
                <TableCell>Device</TableCell>
                <TableCell>MFR Warranty</TableCell>
                <TableCell>L&D Warranty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewProducts.map((product) => {
                const specification =
                  product.inventory_product?.product?.specification;
                const deviceName =
                  product.inventory_product?.product?.display_name ||
                  product.description ||
                  "-";
                if (product.replaced_patient_product_detail) {
                  return (
                    <TableRow
                      key={product.id}
                      data-testid={`warranty-preview-ld-replacement-${product.id}`}
                      sx={{
                        "&:nth-of-type(odd)": { bgcolor: "action.hover" },
                        "&:last-child td": { border: 0 }
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", py: 1.25 }}>
                        {deviceName}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatDateOnlyValue(
                          product.manufacturer_warranty_expiration_date,
                          "MMM dd, yyyy",
                          "-"
                        )}{" "}
                        (carried over, not updated)
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        Used{" "}
                        {product.loss_damage_used_at_display
                          ? `on ${product.loss_damage_used_at_display}`
                          : ""}
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow
                    key={product.id}
                    sx={{
                      "&:nth-of-type(odd)": { bgcolor: "action.hover" },
                      "&:last-child td": { border: 0 }
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", py: 1.25 }}>
                      {deviceName}
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      {formatWarrantyTransition(
                        product.manufacturer_warranty_expiration_date,
                        deliveredAt,
                        specification?.warranty_months
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      {formatWarrantyTransition(
                        product.loss_damage_warranty_expiration_date,
                        deliveredAt,
                        specification?.loss_damage_warranty_months
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
